/**
 * In-app software authenticity check (NF525 §4.3.6, FIS-415).
 *
 * Downloads /release-manifest.json from the deployed origin plus every file
 * it lists, recomputes the SHA-256 hashes with the Web Crypto API, and
 * compares them to the manifest. The recomputed global hash can then be
 * matched against the certified reference held outside the application
 * (the release register entry, or the pos-fr/v<version> and
 * pos-fr-order/v<version> git tags).
 *
 * Same canonical hashing scheme as scripts/generate-release-manifest.mjs.
 */

export interface ReleaseManifest {
  app: string;
  version: string;
  branch: string;
  commit: string;
  buildNumber: string | null;
  buildTime: string;
  algorithm: string;
  fileCount: number;
  globalHash: string;
  files: Record<string, string>;
}

export interface ReleaseVerifyResult {
  ok: boolean;
  fileCount: number;
  version: string;
  commit: string;
  declaredGlobalHash: string;
  recomputedGlobalHash: string;
  failures: string[];
}

const FETCH_TIMEOUT_MS = 60_000;
const FETCH_ATTEMPTS = 2;
const BATCH_SIZE = 8;

function hex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function sha256(buffer: BufferSource): Promise<string> {
  return hex(await crypto.subtle.digest("SHA-256", buffer));
}

async function fetchBytes(origin: string, path: string): Promise<ArrayBuffer> {
  // Retry once: on a slow till a single timeout or network hiccup should not
  // fail the whole authenticity check.
  let lastError: unknown;
  for (let attempt = 0; attempt < FETCH_ATTEMPTS; attempt += 1) {
    try {
      const response = await fetch(`${origin}/${path}`, {
        cache: "no-store",
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.arrayBuffer();
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

export async function verifyDeployedRelease(
  origin: string = window.location.origin
): Promise<ReleaseVerifyResult> {
  const base = origin.replace(/\/+$/, "");
  const manifest = JSON.parse(
    new TextDecoder().decode(await fetchBytes(base, "release-manifest.json"))
  ) as ReleaseManifest;

  const paths = Object.keys(manifest.files);
  const recomputed: Record<string, string> = {};
  const failures: string[] = [];

  for (let i = 0; i < paths.length; i += BATCH_SIZE) {
    await Promise.all(
      paths.slice(i, i + BATCH_SIZE).map(async (path) => {
        try {
          const hash = await sha256(await fetchBytes(base, path));
          recomputed[path] = hash;
          if (hash !== manifest.files[path]) {
            failures.push(`MISMATCH: ${path}`);
          }
        } catch (error) {
          failures.push(
            `UNREACHABLE: ${path} (${
              error instanceof Error ? error.message : String(error)
            })`
          );
        }
      })
    );
  }

  const canonical = Object.keys(recomputed)
    .sort()
    .map((path) => `${recomputed[path]}  ${path}\n`)
    .join("");
  const recomputedGlobalHash = await sha256(
    new TextEncoder().encode(canonical)
  );

  return {
    ok: failures.length === 0 && recomputedGlobalHash === manifest.globalHash,
    fileCount: paths.length,
    version: manifest.version,
    commit: manifest.commit,
    declaredGlobalHash: manifest.globalHash,
    recomputedGlobalHash,
    failures,
  };
}
